// FUNCTIONALITY-101 — Store V2 catalog loader.
// SAME-ORIGIN static fetch of /data/catalog-v2-cards.json (R4.15-B card read model) —
// never the Worker API. Full catalog-v2.json (with `kw`) remains for server/tools.
// Browsing must not depend on /me, session, or cross-origin fetch.
//
// STAGING-LAUNCH-READINESS-002-R2-R2 — two fixes on top of the above:
//  1) every candidate fetch is now bounded (AbortController timeout) so a
//     hung network request can no longer leave the catalog gate spinning
//     forever — it fails fast and falls through to the next candidate URL,
//     then to the existing loadError UI.
//  2) the module-level promise cache gains a TTL. Within the TTL it behaves
//     exactly as before (one fetch per session, fully deduped). Past the TTL,
//     `revalidateCatalogV2IfStale()` refreshes in the background and callers
//     keep serving the already-loaded bundle meanwhile (stale-while-revalidate)
//     — no spinner, no re-fetch-on-every-navigation.

import type {
  CatalogV2Bundle,
  CatalogV2Meta,
  CatalogV2Product,
  RawCatalogV2Meta,
  RawCatalogV2Product,
} from "./catalog-v2-types.js";
import { enrichCatalogWithIdentityOverlay } from "./catalog-identity-overlay-v2.js";
import { enrichCatalogWithStoreSkuMap } from "./catalog-store-sku-map-v2.js";
import { enrichCatalogWithNutritionOverlay } from "./catalog-nutrition-overlay-v2.js";
import { enrichCatalogWithInventoryOverlay } from "./catalog-inventory-overlay-v2.js";
import { enrichCatalogWithLocalProducts, invalidateCustomerLocalProductsCache } from "./catalog-local-products-v2.js";
import { enrichCatalogWithCanaryProducts } from "./catalog-canary-products-v2.js";
import {
  resolveBarcodeVerification,
  type BarcodeVerificationState,
} from "./product-identity-v2.js";
import { buildSearchHaystack } from "./search-v2.js";
import { emitRoutePerf } from "./perf-events-v2.js";
import { cardImageUrl } from "./product-media-v2.js";
import { sortCatalogProducts, type CatalogSortKey } from "./catalog-sort-v2.js";

/** CUSTOMER-R4.15-B — Home/list/card loads; no `kw` payload. */
const CATALOG_FILE = "catalog-v2-cards.json";
const META_FILE = "catalog-v2-meta.json";
/** CUSTOMER-R4.15-C — per-category paginated card shards (same row shape as cards). */
export const CATEGORY_PAGE_SIZE = 36;
const CATALOG_FETCH_TIMEOUT_MS = 8000;
/** How long a loaded bundle is considered fresh before a background revalidate is attempted. */
const CATALOG_TTL_MS = 5 * 60 * 1000;
/** sessionStorage key for last applied catalog revision (revision-aware invalidation). */
const CATALOG_REVISION_SESSION_KEY = "asiastyle.store-v2.catalog_revision";

let cache: Promise<CatalogV2Bundle> | null = null;
let cacheLoadedAt = 0;
let revalidating = false;
let metaCache: Promise<CatalogV2Meta> | null = null;
let appliedCatalogRevision: string | null = null;

// FUNCTIONALITY-105 — WebView diagnostics: prove which catalog URL loaded and the counts.
export interface CatalogDiagnostics {
  catalogUrlTried: string[];
  catalogUrlUsed: string | null;
  catalogHttp: number | null;
  metaUrlUsed: string | null;
  productCount: number;
  categoryCount: number;
  lastError: string | null;
}

const diagnostics: CatalogDiagnostics = {
  catalogUrlTried: [],
  catalogUrlUsed: null,
  catalogHttp: null,
  metaUrlUsed: null,
  productCount: 0,
  categoryCount: 0,
  lastError: null,
};

export function getCatalogDiagnostics(): CatalogDiagnostics {
  return { ...diagnostics, catalogUrlTried: [...diagnostics.catalogUrlTried] };
}

/**
 * Same-origin candidate URLs for a data file. Never the Worker API.
 * 1) route-relative (e.g. /s2-105/data/<file>) 2) absolute canonical (/data/<file>).
 */
function candidateUrls(file: string): string[] {
  const urls: string[] = [];
  try {
    const path = window.location.pathname;
    const base = path.endsWith("/") ? path : path.replace(/[^/]*$/, "");
    if (base && base !== "/") urls.push(`${base}data/${file}`);
  } catch {
    /* ignore */
  }
  urls.push(`/data/${file}`);
  return [...new Set(urls)];
}

/** Map on-disk snake_case meta to the camelCase runtime shape (freshness/debug display). */
function adaptMeta(raw: RawCatalogV2Meta): CatalogV2Meta {
  const buildId = raw.build_id ?? "";
  const catalogRevision =
    (raw.catalog_revision && String(raw.catalog_revision).trim()) ||
    buildId ||
    String(raw.snapshot_id ?? "") ||
    "unknown";
  return {
    buildId,
    catalogRevision,
    snapshotId: raw.snapshot_id ?? null,
    generatedAt: raw.generated_at ?? null,
    fxRate: typeof raw.fx_rate === "number" ? raw.fx_rate : 0,
    productCount: typeof raw.product_count === "number" ? raw.product_count : 0,
    pricedCount: typeof raw.priced_count === "number" ? raw.priced_count : 0,
    categories: Array.isArray(raw.categories) ? raw.categories : [],
  };
}

function readStoredCatalogRevision(): string | null {
  try {
    return window.sessionStorage.getItem(CATALOG_REVISION_SESSION_KEY);
  } catch {
    return null;
  }
}

function writeStoredCatalogRevision(revision: string): void {
  try {
    window.sessionStorage.setItem(CATALOG_REVISION_SESSION_KEY, revision);
  } catch {
    /* ignore quota / private mode */
  }
}

/**
 * CATALOG-SYNC-170A-R2 — if remote meta revision differs from the in-memory /
 * session revision, drop cached catalog + meta so derived local data cannot
 * stick on an old snapshot. No manual ?cachebust / ?reset required.
 */
export function noteCatalogRevision(revision: string): { invalidated: boolean; previous: string | null } {
  const previous = appliedCatalogRevision ?? readStoredCatalogRevision();
  if (previous && previous !== revision) {
    resetCatalogV2Cache();
    appliedCatalogRevision = revision;
    writeStoredCatalogRevision(revision);
    void import("./search-local-index-v2.js")
      .then((m) => m.clearLocalSearchIndex())
      .catch(() => {
        /* best-effort */
      });
    return { invalidated: true, previous };
  }
  appliedCatalogRevision = revision;
  writeStoredCatalogRevision(revision);
  return { invalidated: false, previous };
}

export function getAppliedCatalogRevision(): string | null {
  return appliedCatalogRevision ?? readStoredCatalogRevision();
}

function adapt(raw: RawCatalogV2Product): CatalogV2Product {
  const brand = raw.br ?? null;
  const country = raw.co ?? null;
  const promProductId = String(raw.ppid ?? raw.id ?? "").trim() || null;
  const isAsHash = (v: string | null | undefined) => Boolean(v && /^AS[0-9A-Z]{8}$/i.test(v));
  const merchantSkuRaw = raw.ms?.trim() || null;
  const merchantSku = merchantSkuRaw && !isAsHash(merchantSkuRaw) ? merchantSkuRaw : null;
  const merchantArticle = raw.ma?.trim() || null;
  const barcodeGtin = raw.bc?.trim() || null;
  const barcodeVerification = (raw.bcs as BarcodeVerificationState | undefined) ||
    resolveBarcodeVerification(barcodeGtin, Boolean(barcodeGtin));
  const sourceTitle = raw.tsrc?.trim() || raw.t;
  const customerTitle = raw.ct?.trim() || null;
  const title = customerTitle || raw.t;
  const titleAliases = Array.isArray(raw.tal) ? raw.tal.map(String) : [];
  // Legacy snapshots set sku === prom id; do not treat that as merchant SKU.
  const legacySku = String(raw.sku ?? "").trim();
  const skuLooksLikePromId = Boolean(promProductId && legacySku && legacySku === promProductId);
  const legacyOk =
    !skuLooksLikePromId && legacySku && !isAsHash(legacySku) ? legacySku : null;
  const effectiveMerchantSku = merchantSku || legacyOk;
  return {
    id: raw.id,
    sku: effectiveMerchantSku || "",
    title,
    titleRu: raw.tru ?? null,
    categoryKeys: Array.isArray(raw.c) ? raw.c : [],
    categoryName: raw.cn ?? "",
    imageUrl: cardImageUrl(raw.img) ?? raw.img ?? null,
    productUrl: raw.url ?? null,
    priceUah: raw.pr ? raw.p : null,
    displayPrice: raw.disp,
    isPriced: Boolean(raw.pr),
    stockStatus: raw.st ?? "unknown",
    isSellable: Boolean(raw.sell),
    sellabilityReason: raw.rsn ?? "unknown",
    brand,
    country,
    weight: raw.wt ?? null,
    regularPriceUah: typeof raw.rp === "number" ? raw.rp : null,
    salePriceUah: typeof raw.sp === "number" ? raw.sp : null,
    saleStartsAt: raw.ss ?? null,
    saleEndsAt: raw.se ?? null,
    promotionId: raw.pid ?? null,
    promotionStatus: raw.ps ?? null,
    priceUpdatedAt: raw.pu ?? null,
    sourceRevision: raw.rev ?? null,
    promProductId,
    merchantSku: effectiveMerchantSku,
    merchantArticle,
    barcodeGtin,
    barcodeVerification,
    manufacturerPartNumber: null,
    warehouseCode: null,
    sourceSystem: null,
    sourceUpdatedAt: null,
    lastSyncedAt: null,
    firstSeenAt: raw.fa?.trim() || null,
    catalogArrivedAt: (raw as { fcv?: string }).fcv?.trim() || raw.fa?.trim() || null,
    sourceTitle,
    customerTitle,
    titleAliases,
    storeSkuSource: effectiveMerchantSku ? "prom.sku" : "absent",
    storeSkuVerified: Boolean(effectiveMerchantSku),
    generatedInternalCode: isAsHash(merchantSkuRaw) ? merchantSkuRaw : null,
    search: "", // filled after overlay + haystack rebuild
  };
}

function rebuildSearch(product: CatalogV2Product): CatalogV2Product {
  return {
    ...product,
    search: buildSearchHaystack({
      title: product.title,
      titleRu: product.titleRu,
      categoryName: product.categoryName,
      sku: product.merchantSku || product.merchantArticle,
      brand: product.brand,
      country: product.country,
      barcode: product.barcodeGtin,
      article: product.merchantArticle,
      promProductId: product.promProductId || product.id,
      aliases: product.titleAliases,
    }),
  };
}

/**
 * Fetch JSON trying each same-origin candidate in order. Validates the body is really
 * JSON (SPA fallback returns index.html with 200), so a route rewrite can't masquerade
 * as data. Records the tried URLs + HTTP status into diagnostics.
 */
async function fetchJsonMulti<T>(
  file: string,
  onResult: (url: string, status: number | null) => void,
  externalSignal?: AbortSignal,
  opts?: { cache?: RequestCache; cacheBust?: boolean },
): Promise<{ data: T; url: string }> {
  const urls = candidateUrls(file);
  const cacheMode = opts?.cache ?? "no-store";
  const cacheBust = opts?.cacheBust !== false && cacheMode === "no-store";
  let lastErr = "no_candidate";
  for (const url of urls) {
    if (externalSignal?.aborted) throw new DOMException("Aborted", "AbortError");
    const controller = new AbortController();
    const onExternalAbort = () => controller.abort();
    externalSignal?.addEventListener("abort", onExternalAbort, { once: true });
    const timer = window.setTimeout(() => controller.abort(), CATALOG_FETCH_TIMEOUT_MS);
    try {
      const href = cacheBust ? `${url}?b=${Date.now()}` : url;
      const res = await fetch(href, {
        headers: { Accept: "application/json" },
        cache: cacheMode,
        signal: controller.signal,
      });
      onResult(url, res.status);
      if (!res.ok) {
        lastErr = `http_${res.status}`;
        continue;
      }
      const text = await res.text();
      const trimmed = text.trimStart();
      if (trimmed.startsWith("<")) {
        // SPA fallback (index.html) — not real data at this path.
        lastErr = "html_not_json";
        continue;
      }
      return { data: JSON.parse(trimmed) as T, url };
    } catch (err) {
      if (externalSignal?.aborted) throw new DOMException("Aborted", "AbortError");
      const timedOut = err instanceof DOMException && err.name === "AbortError";
      onResult(url, null);
      lastErr = timedOut ? "fetch_timeout" : err instanceof Error ? err.message : "fetch_failed";
    } finally {
      externalSignal?.removeEventListener("abort", onExternalAbort);
      window.clearTimeout(timer);
    }
  }
  throw new Error(lastErr);
}

async function fetchCatalogBundle(): Promise<CatalogV2Bundle> {
  diagnostics.catalogUrlTried = [];
  const catalog = await fetchJsonMulti<{ products: RawCatalogV2Product[] }>(
    CATALOG_FILE,
    (url, status) => {
      diagnostics.catalogUrlTried.push(`${url} → ${status ?? "err"}`);
      if (status != null) diagnostics.catalogHttp = status;
    },
  );
  diagnostics.catalogUrlUsed = catalog.url;
  const meta = await fetchJsonMulti<RawCatalogV2Meta>(META_FILE, (url) => {
    diagnostics.metaUrlUsed = url;
  });
  const adapted = (catalog.data.products ?? []).map(adapt);
  const withIdentity = await enrichCatalogWithIdentityOverlay(adapted);
  const withStoreSku = await enrichCatalogWithStoreSkuMap(withIdentity);
  const withNutrition = await enrichCatalogWithNutritionOverlay(withStoreSku);
  // CATALOG-PIM-145A-R1D — durable manager inventory overlay (best-effort; never blocks catalog).
  const withInventory = await enrichCatalogWithInventoryOverlay(withNutrition);
  // PRODUCT-PIM-151A-R2 — locally published canonical products (never Prom).
  const enriched = await enrichCatalogWithLocalProducts(withInventory);
  const withCanary = await enrichCatalogWithCanaryProducts(enriched);
  const products = withCanary.map(rebuildSearch);
  const adaptedMeta = adaptMeta(meta.data);
  noteCatalogRevision(adaptedMeta.catalogRevision);
  diagnostics.productCount = products.length;
  diagnostics.categoryCount = adaptedMeta.categories.length;
  diagnostics.lastError = null;
  return { products, meta: adaptedMeta };
}

export function loadCatalogV2(): Promise<CatalogV2Bundle> {
  if (cache) {
    emitRoutePerf({ route: "catalog", stage: "ready", durationMs: 0, cache: "hit" });
    return cache;
  }
  const start = performance.now();
  emitRoutePerf({ route: "catalog", stage: "start", durationMs: 0 });
  cache = fetchCatalogBundle()
    .then((bundle) => {
      cacheLoadedAt = Date.now();
      emitRoutePerf({
        route: "catalog",
        stage: "ready",
        durationMs: performance.now() - start,
        cache: "miss",
      });
      return bundle;
    })
    .catch((err) => {
      cache = null;
      diagnostics.lastError = err instanceof Error ? err.message : "catalog_load_failed";
      const timedOut = err instanceof Error && err.message === "fetch_timeout";
      emitRoutePerf({
        route: "catalog",
        stage: timedOut ? "timeout" : "error",
        durationMs: performance.now() - start,
      });
      throw err;
    });
  return cache;
}

/**
 * VISUAL-SEARCH-001-R4A — home bootstrap: meta-only (~2KB) so shell/Home tiles
 * render before catalog-v2-cards.json parse.
 */
export function loadCatalogMetaV2(): Promise<CatalogV2Meta> {
  if (metaCache) return metaCache;
  metaCache = fetchJsonMulti<RawCatalogV2Meta>(META_FILE, (url) => {
    diagnostics.metaUrlUsed = url;
  })
    .then((meta) => {
      const adapted = adaptMeta(meta.data);
      noteCatalogRevision(adapted.catalogRevision);
      return adapted;
    })
    .catch((err) => {
      metaCache = null;
      throw err;
    });
  return metaCache;
}

/**
 * Explicit reset for the Retry action on a failed catalog load — never
 * `window.location.reload()`, which would re-boot the whole app/session.
 */
export function resetCatalogV2Cache(): void {
  cache = null;
  cacheLoadedAt = 0;
  metaCache = null;
  // AP3 — published Store SKU / local projection must not stay sticky across catalog refresh.
  invalidateCustomerLocalProductsCache();
}

/**
 * Stale-while-revalidate: if the currently-loaded bundle is older than the
 * TTL, kick off a silent background refresh and resolve with the fresh bundle
 * once ready; otherwise resolves to null immediately (nothing to do — the
 * caller should keep rendering what it already has, no spinner either way).
 */
export async function revalidateCatalogV2IfStale(): Promise<CatalogV2Bundle | null> {
  if (!cache || revalidating) return null;
  if (Date.now() - cacheLoadedAt < CATALOG_TTL_MS) return null;
  revalidating = true;
  const start = performance.now();
  emitRoutePerf({ route: "catalog", stage: "start", durationMs: 0, cache: "stale" });
  try {
    const fresh = await fetchCatalogBundle();
    cache = Promise.resolve(fresh);
    cacheLoadedAt = Date.now();
    emitRoutePerf({
      route: "catalog",
      stage: "ready",
      durationMs: performance.now() - start,
      cache: "stale",
    });
    return fresh;
  } catch {
    // Best-effort — keep serving the previously cached bundle.
    return null;
  } finally {
    revalidating = false;
  }
}

export function productsForCategory(bundle: CatalogV2Bundle, categoryId: string): CatalogV2Product[] {
  const rows = bundle.products.filter((p) => p.categoryKeys.includes(categoryId));
  return rows.sort((a, b) => {
    const aCanary = a.sourceSystem === "commerce_canary_163a" ? 0 : 1;
    const bCanary = b.sourceSystem === "commerce_canary_163a" ? 0 : 1;
    if (aCanary !== bCanary) return aCanary - bCanary;
    const aSort = a.canarySortPriority ?? 9999;
    const bSort = b.canarySortPriority ?? 9999;
    if (aSort !== bSort) return aSort - bSort;
    return a.title.localeCompare(b.title, "uk");
  });
}

export type CatalogCategoryPage = {
  categoryId: string;
  sort: string;
  page: number;
  pageSize: number;
  total: number;
  hasMore: boolean;
  nextPage: number | null;
  products: CatalogV2Product[];
};

export type CatalogNewArrivalsPage = {
  page: number;
  pageSize: number;
  total: number;
  hasMore: boolean;
  nextPage: number | null;
  products: CatalogV2Product[];
  sort?: string;
  shardSortMismatch?: boolean;
  /** R7.2.2A — complete newest + client sort when materialized shard missing. */
  sortFallback?: "complete_newest_client_sort";
};

type RawNewArrivalsPage = {
  schema?: string;
  page?: number;
  page_size?: number;
  total?: number;
  has_more?: boolean;
  next_page?: number | null;
  sort?: string;
  products?: RawCatalogV2Product[];
};

type RawCategoryPage = {
  schema?: string;
  category_id?: string;
  sort?: string;
  page?: number;
  page_size?: number;
  total?: number;
  has_more?: boolean;
  next_page?: number | null;
  products?: RawCatalogV2Product[];
};

/**
 * CUSTOMER-R4.15-C — incremental category read model.
 * CUSTOMER-R4.15-E — sort-keyed shards: FILTER → SORT ALL → PAGINATE on emit.
 * CUSTOMER-R4.15-K8 — exact-key in-memory cache + inflight dedupe + HTTP cache (no cache-bust).
 * Fetches `/data/catalog-v2-category/{id}/{sort}/p{n}.json` (not full cards/full catalog).
 * List cards use adapt() only — search haystack is not needed for category grid.
 */
export const POPULAR_CATEGORY_PREFETCH_IDS = [
  "biz_ramen_buldak",
  "biz_snacks",
  "biz_asian_tea",
  "biz_sauces",
  "biz_drinks",
] as const;

const CATEGORY_PAGE_TTL_MS = 60_000;
type CachedCategoryPage = { value: CatalogCategoryPage; loadedAt: number };
const categoryResolved = new Map<string, CachedCategoryPage>();
const categoryInflight = new Map<string, Promise<CatalogCategoryPage>>();

export function categoryShardCacheKey(categoryId: string, page = 1, sort = "recommended"): string {
  const id = String(categoryId ?? "").trim();
  const pageNum = Math.max(1, Math.floor(Number(page) || 1));
  const sortKey = String(sort ?? "recommended").trim() || "recommended";
  return `${id}|${sortKey}|${pageNum}`;
}

export function peekCategoryPageCache(
  categoryId: string,
  page = 1,
  sort = "recommended",
): CatalogCategoryPage | null {
  return categoryResolved.get(categoryShardCacheKey(categoryId, page, sort))?.value ?? null;
}

export function prefetchCategoryPage(categoryId: string, sort = "recommended"): void {
  void loadCategoryPage(categoryId, 1, sort);
}

export async function loadCategoryPage(
  categoryId: string,
  page = 1,
  sort: string = "recommended",
  signal?: AbortSignal,
): Promise<CatalogCategoryPage> {
  const id = String(categoryId ?? "").trim();
  const pageNum = Math.max(1, Math.floor(Number(page) || 1));
  const sortKey = String(sort ?? "recommended").trim() || "recommended";
  if (!id) throw new Error("category_id_required");
  const key = categoryShardCacheKey(id, pageNum, sortKey);
  const cached = categoryResolved.get(key);
  const fresh = Boolean(cached && Date.now() - cached.loadedAt < CATEGORY_PAGE_TTL_MS);
  if (cached && fresh) {
    emitRoutePerf({ route: "category", stage: "ready", durationMs: 0, cache: "hit" });
    return cached.value;
  }
  const inflight = categoryInflight.get(key);
  if (inflight) return inflight;
  const pending = loadCategoryPageUncached(id, pageNum, sortKey, signal)
    .then((value) => {
      categoryResolved.set(key, { value, loadedAt: Date.now() });
      return value;
    })
    .catch((err) => {
      categoryInflight.delete(key);
      throw err;
    })
    .finally(() => {
      categoryInflight.delete(key);
    });
  categoryInflight.set(key, pending);
  if (cached && !fresh) {
    void pending.catch(() => {
      /* keep stale on revalidate failure */
    });
    emitRoutePerf({ route: "category", stage: "ready", durationMs: 0, cache: "stale" });
    return cached.value;
  }
  return pending;
}

async function loadCategoryPageUncached(
  id: string,
  pageNum: number,
  sortKey: string,
  signal?: AbortSignal,
): Promise<CatalogCategoryPage> {
  const file = `catalog-v2-category/${encodeURIComponent(id)}/${encodeURIComponent(sortKey)}/p${pageNum}.json`;
  const start = performance.now();
  emitRoutePerf({ route: "category", stage: "start", durationMs: 0 });
  try {
    const fetched = await fetchJsonMulti<RawCategoryPage>(
      file,
      () => {
        /* per-file diagnostics not required for category shards */
      },
      signal,
      { cache: "force-cache", cacheBust: false },
    );
    const raw = fetched.data;
    const products = (raw.products ?? []).map((p) => adapt(p));
    const total = typeof raw.total === "number" ? raw.total : products.length;
    const pageSize = typeof raw.page_size === "number" ? raw.page_size : CATEGORY_PAGE_SIZE;
    const hasMore =
      typeof raw.has_more === "boolean" ? raw.has_more : pageNum * pageSize < total;
    const nextPage =
      raw.next_page != null
        ? Number(raw.next_page)
        : hasMore
          ? pageNum + 1
          : null;
    emitRoutePerf({
      route: "category",
      stage: "ready",
      durationMs: performance.now() - start,
      cache: "miss",
    });
    return {
      categoryId: String(raw.category_id ?? id),
      sort: String(raw.sort ?? sortKey),
      page: typeof raw.page === "number" ? raw.page : pageNum,
      pageSize,
      total,
      hasMore,
      nextPage,
      products,
    };
  } catch (err) {
    emitRoutePerf({
      route: "category",
      stage: "error",
      durationMs: performance.now() - start,
    });
    throw err;
  }
}

/**
 * K3 — incremental New Arrivals read model.
 * Fetches `/data/catalog-v2-new-arrivals/p{n}.json` and never depends on full catalog cards blob.
 * p1 is memoized so Home idle-prefetch and route open share one request.
 */
const newArrivalsPageCache = new Map<string, Promise<CatalogNewArrivalsPage>>();

export function prefetchNewArrivalsPage1(): void {
  void loadNewArrivalsPage(1, "newest");
}

export async function loadNewArrivalsPage(
  page = 1,
  sortRaw: string = "newest",
): Promise<CatalogNewArrivalsPage> {
  const pageNum = Math.max(1, Math.floor(Number(page) || 1));
  const sortKey = String(sortRaw ?? "newest").trim() || "newest";
  const cacheKey = `${sortKey}|${pageNum}`;
  const cached = newArrivalsPageCache.get(cacheKey);
  if (cached) return cached;
  const pending = loadNewArrivalsPageUncached(pageNum, sortKey).catch((err) => {
    newArrivalsPageCache.delete(cacheKey);
    throw err;
  });
  newArrivalsPageCache.set(cacheKey, pending);
  return pending;
}

async function fetchIncrementalNewArrivalsPage(
  pageNum: number,
  sortKey: string,
): Promise<CatalogNewArrivalsPage | null> {
  try {
    const res = await fetch(
      `/api/store-v2/catalog/incremental-new-arrivals?page=${pageNum}&sort=${encodeURIComponent(sortKey)}`,
      { cache: "no-store", headers: { Accept: "application/json" } },
    );
    if (!res.ok) return null;
    const raw = (await res.json()) as {
      ok?: boolean;
      sort?: string;
      page?: number;
      page_size?: number;
      total?: number;
      has_more?: boolean;
      next_page?: number | null;
      products?: RawCatalogV2Product[];
    };
    if (!raw.ok || !Array.isArray(raw.products)) return null;
    const products = raw.products.map((p) => rebuildSearch(adapt(p)));
    const pageSize = typeof raw.page_size === "number" ? raw.page_size : 24;
    const total = typeof raw.total === "number" ? raw.total : products.length;
    const hasMore = typeof raw.has_more === "boolean" ? raw.has_more : pageNum * pageSize < total;
    return {
      page: typeof raw.page === "number" ? raw.page : pageNum,
      pageSize,
      total,
      hasMore,
      nextPage: raw.next_page != null ? Number(raw.next_page) : hasMore ? pageNum + 1 : null,
      products,
      sort: String(raw.sort ?? sortKey),
    };
  } catch {
    return null;
  }
}

function mergeIncrementalNewest(
  staticPage: CatalogNewArrivalsPage,
  incremental: CatalogNewArrivalsPage | null,
): CatalogNewArrivalsPage {
  if (!incremental?.products.length) return staticPage;
  const seen = new Set(staticPage.products.map((p) => p.id));
  const merged = [
    ...incremental.products.filter((p) => !seen.has(p.id)),
    ...staticPage.products,
  ];
  merged.sort((a, b) => {
    const ta = Date.parse(String(a.catalogArrivedAt ?? a.firstSeenAt ?? ""));
    const tb = Date.parse(String(b.catalogArrivedAt ?? b.firstSeenAt ?? ""));
    return (Number.isFinite(tb) ? tb : 0) - (Number.isFinite(ta) ? ta : 0);
  });
  const slice = merged.slice(0, staticPage.pageSize);
  return {
    ...staticPage,
    products: slice,
    total: Math.max(staticPage.total, merged.length),
    hasMore: merged.length > slice.length || staticPage.hasMore,
  };
}

async function loadNewArrivalsPageUncached(
  pageNum: number,
  sortKey: string,
): Promise<CatalogNewArrivalsPage> {
  const preferred =
    sortKey === "newest"
      ? `catalog-v2-new-arrivals/p${pageNum}.json`
      : `catalog-v2-new-arrivals/${encodeURIComponent(sortKey)}/p${pageNum}.json`;
  const start = performance.now();
  emitRoutePerf({ route: "new_arrivals", stage: "start", durationMs: 0 });
  try {
    let raw: RawNewArrivalsPage;
    let usedLegacyNewest = false;
    try {
      const fetched = await fetchJsonMulti<RawNewArrivalsPage>(preferred, () => {
        /* no-op */
      });
      raw = fetched.data;
    } catch {
      // Legacy: only newest pages at root — caller may load-all + local sort.
      usedLegacyNewest = true;
      const fetched = await fetchJsonMulti<RawNewArrivalsPage>(`catalog-v2-new-arrivals/p${pageNum}.json`, () => {
        /* no-op */
      });
      raw = fetched.data;
    }
    const products = (raw.products ?? []).map((p) => rebuildSearch(adapt(p)));
    const pageSize = typeof raw.page_size === "number" ? raw.page_size : 24;
    const total = typeof raw.total === "number" ? raw.total : products.length;
    const hasMore = typeof raw.has_more === "boolean" ? raw.has_more : pageNum * pageSize < total;
    const nextPage = raw.next_page != null ? Number(raw.next_page) : hasMore ? pageNum + 1 : null;
    const shardSortMismatch = usedLegacyNewest && sortKey !== "newest";
    if (shardSortMismatch) {
      // R7.2.2A — NEVER substitute tiny incremental live-tail for a missing sort shard.
      // Load complete newest universe and sort client-side (same N cardinality).
      try {
        const all = await loadAllNewArrivalsProducts({ maxPages: 80 });
        const titleOf = (p: CatalogV2Product) => String(p.title ?? "");
        const sorted = sortCatalogProducts(all.products, sortKey as CatalogSortKey, titleOf);
        const pageSize = typeof raw.page_size === "number" ? raw.page_size : 24;
        const startIdx = (pageNum - 1) * pageSize;
        const slice = sorted.slice(startIdx, startIdx + pageSize);
        const total = sorted.length;
        const hasMore = startIdx + pageSize < total;
        emitRoutePerf({
          route: "new_arrivals",
          stage: "ready",
          durationMs: performance.now() - start,
          cache: "miss",
        });
        return {
          page: pageNum,
          pageSize,
          total,
          hasMore,
          nextPage: hasMore ? pageNum + 1 : null,
          products: slice,
          sort: sortKey,
          shardSortMismatch: false,
          sortFallback: "complete_newest_client_sort",
        };
      } catch {
        emitRoutePerf({
          route: "new_arrivals",
          stage: "ready",
          durationMs: performance.now() - start,
          cache: "miss",
        });
        return {
          page: pageNum,
          pageSize,
          total: 0,
          hasMore: false,
          nextPage: null,
          products: [],
          sort: sortKey,
          shardSortMismatch: true,
        };
      }
    }
    const basePage: CatalogNewArrivalsPage = {
      page: typeof raw.page === "number" ? raw.page : pageNum,
      pageSize,
      total,
      hasMore,
      nextPage,
      products,
      sort: usedLegacyNewest ? String(raw.sort ?? "newest") : String(raw.sort ?? sortKey),
      shardSortMismatch,
    };
    if (sortKey === "newest" && pageNum === 1) {
      const incremental = await fetchIncrementalNewArrivalsPage(1, "newest");
      emitRoutePerf({
        route: "new_arrivals",
        stage: "ready",
        durationMs: performance.now() - start,
        cache: "miss",
      });
      return mergeIncrementalNewest(basePage, incremental);
    }
    emitRoutePerf({
      route: "new_arrivals",
      stage: "ready",
      durationMs: performance.now() - start,
      cache: "miss",
    });
    return basePage;
  } catch (err) {
    emitRoutePerf({
      route: "new_arrivals",
      stage: "error",
      durationMs: performance.now() - start,
    });
    throw err;
  }
}

export async function loadAllNewArrivalsProducts(opts?: {
  maxPages?: number;
}): Promise<{ products: CatalogV2Product[]; total: number }> {
  const maxPages = Math.min(100, Math.max(1, opts?.maxPages ?? 80));
  const all: CatalogV2Product[] = [];
  let total = 0;
  let page = 1;
  let hasMore = true;
  while (hasMore && page <= maxPages) {
    const shard = await loadNewArrivalsPage(page, "newest");
    total = shard.total;
    const seen = new Set(all.map((p) => p.id));
    for (const p of shard.products) {
      if (!seen.has(p.id)) all.push(p);
    }
    hasMore = shard.hasMore;
    page += 1;
  }
  return { products: all, total: total || all.length };
}

/** CUSTOMER-R4.15-K7 — lightweight brand directory (no full catalog download). */
export type BrandIndexRow = {
  brand_id: string;
  name: string;
  slug: string;
  productCount: number;
  brand_country: string | null;
};

type RawBrandIndex = {
  brands?: BrandIndexRow[];
};

let brandIndexCache: Promise<BrandIndexRow[]> | null = null;

export function prefetchBrandIndexV2(): void {
  void loadBrandIndexV2();
}

export async function loadBrandIndexV2(): Promise<BrandIndexRow[]> {
  if (!brandIndexCache) {
    brandIndexCache = fetchJsonMulti<RawBrandIndex>(
      "catalog-v2-brands-index.json",
      () => {
        /* shard diagnostics optional */
      },
      undefined,
      { cache: "force-cache", cacheBust: false },
    )
      .then((fetched) => (Array.isArray(fetched.data.brands) ? fetched.data.brands : []))
      .catch(() => {
        brandIndexCache = null;
        return [] as BrandIndexRow[];
      });
  }
  return brandIndexCache;
}

/** CUSTOMER-R4.15-K10 — lightweight cuisine/country directory (no full catalog). */
export type CountryIndexRow = {
  country_id: string;
  label: string;
  labelUk: string;
  labelEn: string;
  iso2: string | null;
  flagEmoji: string;
  meaning?: string;
  productCount: number;
  activeAvailabilityCount: number;
};

type RawCountryIndex = {
  countries?: CountryIndexRow[];
};

let countryIndexCache: Promise<CountryIndexRow[]> | null = null;

export function prefetchCountryIndexV2(): void {
  void loadCountryIndexV2();
}

export async function loadCountryIndexV2(): Promise<CountryIndexRow[]> {
  if (!countryIndexCache) {
    countryIndexCache = fetchJsonMulti<RawCountryIndex>(
      "catalog-v2-countries-index.json",
      () => {
        /* shard diagnostics optional */
      },
      undefined,
      { cache: "force-cache", cacheBust: false },
    )
      .then((fetched) => (Array.isArray(fetched.data.countries) ? fetched.data.countries : []))
      .catch(() => {
        countryIndexCache = null;
        return [] as CountryIndexRow[];
      });
  }
  return countryIndexCache;
}

/**
 * CUSTOMER-R4.15-K10 — brand/country product pages (recommended sort only).
 * Exact-key cache + inflight dedupe; never the 2.77 MB cards blob.
 */
export type CatalogDiscoveryPage = {
  id: string;
  sort: string;
  page: number;
  pageSize: number;
  total: number;
  hasMore: boolean;
  nextPage: number | null;
  products: CatalogV2Product[];
};

type RawDiscoveryPage = {
  brand_id?: string;
  country_id?: string;
  sort?: string;
  page?: number;
  page_size?: number;
  total?: number;
  has_more?: boolean;
  next_page?: number | null;
  products?: RawCatalogV2Product[];
};

const DISCOVERY_PAGE_TTL_MS = 60_000;
type CachedDiscoveryPage = { value: CatalogDiscoveryPage; loadedAt: number };
const discoveryResolved = new Map<string, CachedDiscoveryPage>();
const discoveryInflight = new Map<string, Promise<CatalogDiscoveryPage>>();

export function discoveryShardCacheKey(
  kind: "brand" | "country",
  id: string,
  page = 1,
  sort = "recommended",
): string {
  const sortKey = String(sort ?? "recommended").trim() || "recommended";
  return `${kind}|${String(id ?? "").trim()}|${sortKey}|${Math.max(1, Math.floor(Number(page) || 1))}`;
}

export function peekBrandPageCache(slug: string, page = 1, sort = "recommended"): CatalogDiscoveryPage | null {
  return discoveryResolved.get(discoveryShardCacheKey("brand", slug, page, sort))?.value ?? null;
}

export function peekCountryPageCache(
  countryId: string,
  page = 1,
  sort = "recommended",
): CatalogDiscoveryPage | null {
  return discoveryResolved.get(discoveryShardCacheKey("country", countryId, page, sort))?.value ?? null;
}

export function prefetchBrandPage(slug: string, sort = "recommended"): void {
  void loadBrandPage(slug, 1, sort);
}

export function prefetchCountryPage(countryId: string, sort = "recommended"): void {
  void loadCountryPage(countryId, 1, sort);
}

export async function loadBrandPage(
  slug: string,
  page = 1,
  sortOrSignal?: string | AbortSignal,
  signal?: AbortSignal,
): Promise<CatalogDiscoveryPage> {
  const sort = typeof sortOrSignal === "string" ? sortOrSignal : "recommended";
  const abort = typeof sortOrSignal === "object" ? sortOrSignal : signal;
  return loadDiscoveryPage("brand", slug, page, sort, abort);
}

export async function loadCountryPage(
  countryId: string,
  page = 1,
  sortOrSignal?: string | AbortSignal,
  signal?: AbortSignal,
): Promise<CatalogDiscoveryPage> {
  const sort = typeof sortOrSignal === "string" ? sortOrSignal : "recommended";
  const abort = typeof sortOrSignal === "object" ? sortOrSignal : signal;
  return loadDiscoveryPage("country", countryId, page, sort, abort);
}

async function loadDiscoveryPage(
  kind: "brand" | "country",
  idRaw: string,
  page: number,
  sortRaw = "recommended",
  signal?: AbortSignal,
): Promise<CatalogDiscoveryPage> {
  const id = String(idRaw ?? "").trim();
  const pageNum = Math.max(1, Math.floor(Number(page) || 1));
  const sortKey = String(sortRaw ?? "recommended").trim() || "recommended";
  if (!id) throw new Error(`${kind}_id_required`);
  const key = discoveryShardCacheKey(kind, id, pageNum, sortKey);
  const cached = discoveryResolved.get(key);
  const fresh = Boolean(cached && Date.now() - cached.loadedAt < DISCOVERY_PAGE_TTL_MS);
  if (cached && fresh) {
    emitRoutePerf({ route: kind, stage: "ready", durationMs: 0, cache: "hit" });
    return cached.value;
  }
  const inflight = discoveryInflight.get(key);
  if (inflight) return inflight;
  const pending = loadDiscoveryPageUncached(kind, id, pageNum, sortKey, signal)
    .then((value) => {
      discoveryResolved.set(key, { value, loadedAt: Date.now() });
      return value;
    })
    .catch((err) => {
      discoveryInflight.delete(key);
      throw err;
    })
    .finally(() => {
      discoveryInflight.delete(key);
    });
  discoveryInflight.set(key, pending);
  if (cached && !fresh) {
    void pending.catch(() => {
      /* keep stale */
    });
    emitRoutePerf({ route: kind, stage: "ready", durationMs: 0, cache: "stale" });
    return cached.value;
  }
  return pending;
}

async function loadDiscoveryPageUncached(
  kind: "brand" | "country",
  id: string,
  pageNum: number,
  sortKey: string,
  signal?: AbortSignal,
): Promise<CatalogDiscoveryPage> {
  const folder = kind === "brand" ? "catalog-v2-brand" : "catalog-v2-country";
  const preferred = `${folder}/${encodeURIComponent(id)}/${encodeURIComponent(sortKey)}/p${pageNum}.json`;
  const legacy = `${folder}/${encodeURIComponent(id)}/recommended/p${pageNum}.json`;
  const start = performance.now();
  emitRoutePerf({ route: kind, stage: "start", durationMs: 0 });
  try {
    let raw: RawDiscoveryPage;
    let usedLegacyRecommended = false;
    try {
      const fetched = await fetchJsonMulti<RawDiscoveryPage>(
        preferred,
        () => {
          /* noop */
        },
        signal,
        { cache: "force-cache", cacheBust: false },
      );
      raw = fetched.data;
    } catch {
      // Pre-R6.9 shards: only recommended exists — never claim requested sort.
      usedLegacyRecommended = true;
      const fetched = await fetchJsonMulti<RawDiscoveryPage>(
        legacy,
        () => {
          /* noop */
        },
        signal,
        { cache: "force-cache", cacheBust: false },
      );
      raw = fetched.data;
    }
    const products = (raw.products ?? []).map((p) => adapt(p));
    const total = typeof raw.total === "number" ? raw.total : products.length;
    const pageSize = typeof raw.page_size === "number" ? raw.page_size : CATEGORY_PAGE_SIZE;
    const hasMore = typeof raw.has_more === "boolean" ? raw.has_more : pageNum * pageSize < total;
    const nextPage = raw.next_page != null ? Number(raw.next_page) : hasMore ? pageNum + 1 : null;
    emitRoutePerf({
      route: kind,
      stage: "ready",
      durationMs: performance.now() - start,
      cache: "miss",
    });
    return {
      id: String((kind === "brand" ? raw.brand_id : raw.country_id) ?? id),
      sort: usedLegacyRecommended
        ? String(raw.sort ?? "recommended")
        : String(raw.sort ?? sortKey),
      page: typeof raw.page === "number" ? raw.page : pageNum,
      pageSize,
      total,
      hasMore,
      nextPage,
      products,
    };
  } catch (err) {
    emitRoutePerf({
      route: kind,
      stage: "error",
      durationMs: performance.now() - start,
    });
    throw err;
  }
}

/** R6.9 — load complete brand/country set then client-sort when shards lack sort keys. */
export async function loadAllDiscoveryProducts(
  kind: "brand" | "country",
  id: string,
  opts?: { maxPages?: number; signal?: AbortSignal },
): Promise<{ products: CatalogV2Product[]; total: number; pageSize: number }> {
  const maxPages = Math.min(100, Math.max(1, opts?.maxPages ?? 80));
  const all: CatalogV2Product[] = [];
  let total = 0;
  let pageSize = CATEGORY_PAGE_SIZE;
  let page = 1;
  let hasMore = true;
  while (hasMore && page <= maxPages) {
    const shard = await loadDiscoveryPage(kind, id, page, "recommended", opts?.signal);
    total = shard.total;
    pageSize = shard.pageSize;
    const seen = new Set(all.map((p) => p.id));
    for (const p of shard.products) {
      if (!seen.has(p.id)) all.push(p);
    }
    hasMore = shard.hasMore;
    page += 1;
  }
  return { products: all, total: total || all.length, pageSize };
}

